<template>
  <div>
    <v-card class="pa-4" style="margin: 16px">
      <v-card-title primary-title>
        <div class="headline">{{ $t("transferCentersPage.createTitle") }}</div>
      </v-card-title>

      <v-form ref="form" v-model="form">
        <v-layout wrap>
          <v-flex xs12 md6 class="px-3">
            <v-text-field v-model="model.name" :label='$t("transferCentersPage.name")' :rules="[rules.required]" ></v-text-field>

            <v-text-field v-model="model.username" :label='$t("transferCentersPage.email")' type="email"
              :rules="[rules.required, rules.email]" ></v-text-field>

            <v-text-field v-model="model.phone_number" :label='$t("transferCentersPage.phoneNumber")' type="tel"
              :rules="[rules.required, rules.phoneNumber]" ></v-text-field>

            <v-text-field v-model="model.password" :label='$t("transferCentersPage.password")' type="password"
              :rules="[rules.required, rules.password]" ></v-text-field>

            <v-text-field v-model="model.password_confirmation" :label='$t("transferCentersPage.passwordConfirmation")' type="password"
              :rules="[rules.required, rules.passwordConfirmation]" ></v-text-field>

            <v-textarea v-model="model.address" :label='$t("transferCentersPage.address")' :rules="[rules.required]" rows="3" auto-grow
              ></v-textarea>

            <v-select v-model="model.governorate_id" :label='$t("transferCentersPage.governorate")'
              :items="governorateItems" item-text="name" item-value="id" clearable ></v-select>

            <!-- Accountant doc 2026-10-03 item 1.9: the company's own (Snap)
                 center also sends gifts from a separate gift budget. -->
            <v-switch v-model="model.is_company" color="primary"
              :label='$t("snapCenterGifts.isCompany")'
              :hint='$t("snapCenterGifts.isCompanyHint")' persistent-hint></v-switch>
          </v-flex>

          <v-flex xs12 md6 class="px-3">
            <div class="map-label">{{ $t("transferCentersPage.locationDrag") }}</div>
            <div id="create-tc-map"></div>
          </v-flex>
        </v-layout>

        <v-layout justify-end class="px-3">
          <v-btn flat @click="$router.push('/transfer-centers')">{{ $t("transferCentersPage.cancel") }}</v-btn>
          <v-btn color="primary" :loading="loading" @click="create">{{ $t("transferCentersPage.create") }}</v-btn>
        </v-layout>
      </v-form>
    </v-card>
  </div>
</template>

<script>
import Mapbox from "mapbox-gl";

export default {
  name: "TransferCenterCreate",
  title() {
    return this.$t("transferCentersPage.createTitle");
  },
  data() {
    return {
      loading: false,
      form: false,
      map: null,
      marker: null,
      location: null,
      governorates: [],
      model: {
        name: "",
        username: "",
        phone_number: "",
        password: "",
        password_confirmation: "",
        address: "",
        governorate_id: null,
        is_company: false,
      },
    };
  },
  mounted() {
    this.$nextTick(() => this.loadMap());
    this.axios
      .get(this.api.ACTIONS.GOVERNORATE || "governorate")
      .then((response) => {
        this.governorates = response.data.data;
      })
      .catch((error) => {
        this.governorates = [];
        console.warn("governorate list unavailable", error);
      });
  },
  computed: {
    governorateItems() {
      return this.governorates.map((g) => ({
        id: g.id,
        name:
          (this.$i18n && this.$i18n.locale === "ar"
            ? g.name_ar || g.name_en
            : g.name_en || g.name_ar) || `#${g.id}`,
      }));
    },
    rules() {
      return {
        required: (v) => !!v || this.$t("transferCentersPage.fieldRequired"),
        email: (v) =>
          /.+@.+\..+/.test(v || "") || this.$t("transferCentersPage.validEmail"),
        password: (v) =>
          (v || "").length >= 8 || this.$t("transferCentersPage.passwordMin"),
        passwordConfirmation: (v) =>
          v === this.model.password ||
          this.$t("transferCentersPage.passwordsNoMatch"),
        phoneNumber: (v) =>
          /^(00|\+)?[1-9]\d{6,}$/.test(v || "") ||
          this.$t("transferCentersPage.invalidPhone"),
      };
    },
  },
  methods: {
    loadMap() {
      Mapbox.accessToken = this.api.mapBox.accessToken;
      const initialCoords = this.api.mapBox.center;
      this.location = [initialCoords[0], initialCoords[1]];
      this.map = new Mapbox.Map(
        Object.assign({ container: "create-tc-map" }, this.api.mapBox, {
          center: initialCoords,
        })
      );
      this.marker = new Mapbox.Marker({ draggable: true });
      this.marker.setLngLat(initialCoords);
      this.marker.addTo(this.map);
      this.marker.on("dragend", () => {
        const lngLat = this.marker.getLngLat();
        this.location = [lngLat.lng, lngLat.lat];
      });
    },
    create() {
      if (!this.$refs.form.validate()) return;
      if (!this.location) {
        window.getApp.$emit(
          "SHOW_TOAST",
          this.$t("transferCentersPage.setLocationToast"),
          "red"
        );
        return;
      }

      this.loading = true;
      const payload = {
        ...this.model,
        location: { type: "Point", coordinates: this.location },
      };

      this.axios
        .post("transfer-center", payload)
        .then(({ data }) => {
          window.getApp.$emit(
            "SHOW_TOAST",
            this.$t("transferCentersPage.createdToast"),
            "green"
          );
          const newId = data && data.data && data.data.id;
          if (newId) {
            this.$router.push(`/transfer-centers/${newId}`);
          } else {
            this.$router.push("/transfer-centers");
          }
        })
        .catch((err) => {
          const msg =
            (err.response && err.response.data && err.response.data.message) ||
            this.$t("transferCentersPage.createFailedToast");
          window.getApp.$emit("SHOW_TOAST", msg, "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.map-label {
  font-size: 14px;
  color: #202731;
  margin-bottom: 8px;
}

#create-tc-map {
  width: 100%;
  height: 500px;
  border-radius: 8px;
}
</style>
